import React, { useState, useEffect, useMemo } from "react";
import { Toaster, toast } from "react-hot-toast";
import { Loader, useWindowSize, Wrapper } from "../common";
import CommunityBrandHeader from "./CommunityBrandHeader";
import { formatI18nKeys, toI18N } from "../../i18n";
import { useCooldown } from "../../hooks/useCooldown";
import fetchApi from "../../services/FetchApi";
import { authErrorMessage } from "../../utils/authErrors";
import { getInfoFormIntlPhoneNumber } from "../../utils/helpers";
import phone from 'phone';
import countryCodeList from '../../utils/CountryCodeList';

// Validate verification code format (6 digits only)
const isValidVerificationCode = (code: string): boolean => {
  return /^\d{6}$/.test(code);
};

export type PhoneVerificationProps = {
  userId: string;
  email: string;
  jwtToken: string;
  coverImage?: string;
  backgroundColor?: string;
  logo?: string;
  title?: string;
  handleBack: () => void;
  handlePhoneVerificationComplete: (userId: string, refreshToken: string, refreshTokenV2?: string, birthday?: string) => void;
  checkingLocation: boolean;
  // Pending-signup mode: no account/JWT yet. The SMS code is verified against
  // the pending-signup token and the phone is folded into a new token,
  // delivered through handlePendingPhoneVerified.
  signupToken?: string;
  handlePendingPhoneVerified?: (signupToken: string) => void;
};

export default function PhoneVerification(props: PhoneVerificationProps) {
  const [phoneNumber, setPhoneNumber] = useState<string>("");
  const [phoneNumberValid, setPhoneNumberValid] = useState<boolean>(false);
  const [countryCode, setCountryCode] = useState<string>("+86"); // Default to China
  const [country, setCountry] = useState<string>("CN"); // Default to China
  const [verificationCode, setVerificationCode] = useState<string>("");
  const [showCodeInput, setShowCodeInput] = useState<boolean>(false);
  // Field-attributable errors render inline under their input (persistent,
  // assertive), matching the email-code and age screens — not as toasts.
  const [phoneError, setPhoneError] = useState<string>("");
  const [codeError, setCodeError] = useState<string>("");
  const [phoneVerificationLoading, setPhoneVerificationLoading] = useState<boolean>(false);
  const [inIframe, setInIframe] = useState(false);
  const size = useWindowSize();
  const verifyLock = useCooldown(0);
  const resend = useCooldown(0);
  const [resendLoading, setResendLoading] = useState(false);

  // Handle back navigation based on current state
  const handleBackNavigation = () => {
    if (showCodeInput) {
      // If on verification code input screen, go back to phone number input
      setShowCodeInput(false);
      setVerificationCode(""); // Clear the verification code when going back
      setCodeError("");
    } else {
      // If on phone number input screen, go back to previous step in signup flow
      props.handleBack();
    }
  };

  const [countryQuery, setCountryQuery] = useState<string>("");
  const [countryDropdownOpen, setCountryDropdownOpen] = useState<boolean>(false);
  const [highlightIndex, setHighlightIndex] = useState<number>(0);

  useEffect(() => {
    const item = countryCodeList.find((i) => i.value === `${countryCode}|${country}`);
    setCountryQuery(item ? item.label : `${countryCode} (${country})`);
  }, [countryCode, country]);

  const { formatPhoneNumber } = getInfoFormIntlPhoneNumber(
    phoneNumber,
    countryCode
  );

  const filteredCountries = useMemo(() => {
    const q = countryQuery.toLowerCase().trim();
    if (!q) return countryCodeList;
    return countryCodeList.filter((item) => {
      const label = String(item.label || "").toLowerCase();
      const name = String(item.name || "").toLowerCase();
      const code = String(item.alpha2Code || "").toLowerCase();
      return label.includes(q) || name.includes(q) || code.includes(q);
    });
  }, [countryQuery]);

  useEffect(() => {
    setInIframe(window.top !== window.self);
  }, []);

  useEffect(() => {
    if (showCodeInput) resend.start(30);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCodeInput]);

  const handleSendCode = async () => {
    if (!formatPhoneNumber || formatPhoneNumber.length < 5) {
      setPhoneError(toI18N("lp_login_phone_invalid"));
      return;
    }

    setPhoneVerificationLoading(true);
    setPhoneError("");
    try {
      const result = await fetchApi.requestSmsToken(`${countryCode}${formatPhoneNumber}`, countryCode);

      setShowCodeInput(true);
      if (result.token) {
        setVerificationCode(result.token);
      }

      toast.success(toI18N("lp_login_phone_verification_sent"));
    } catch (error: any) {
      setPhoneError(authErrorMessage(error));
    } finally {
      setPhoneVerificationLoading(false);
    }
  };

  const handleResend = async () => {
    if (resend.active || resendLoading) return;
    setResendLoading(true);
    try {
      const data = await fetchApi.requestSmsToken(`${countryCode}${formatPhoneNumber}`, countryCode);
      if (data?.token) {
        toast.success(toI18N("lp_login_otp_resend_success_toast"));
        resend.start(Number(data?.backoff ?? data?.time) || 30);
      } else {
        resend.start(Number(data?.backoff ?? data?.time) || 30);
      }
    } catch (e) {
      setCodeError(authErrorMessage(e));
    } finally {
      setResendLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!isValidVerificationCode(verificationCode)) {
      setCodeError(toI18N("lp_login_phone_code_invalid"));
      return;
    }

    setPhoneVerificationLoading(true);
    setCodeError("");
    try {
      const result = props.signupToken
        ? await fetchApi.verifySignupSmsToken(
            `${countryCode}${formatPhoneNumber}`,
            countryCode,
            verificationCode,
            props.signupToken
          )
        : await fetchApi.verifySmsToken(
            `${countryCode}${formatPhoneNumber}`,
            countryCode,
            verificationCode,
            props.userId,
            props.jwtToken
          );

      // 200 {"backoff": N} is the verify throttle, not a login. Treating it as
      // a user object previously advanced the flow with no session (phantom
      // age screen). Lock the Verify button for N seconds instead.
      if (result?.backoff != null) {
        verifyLock.start(Number(result.backoff) || 30);
        return;
      }

      if (props.signupToken) {
        if (result?.signup_token) {
          props.handlePendingPhoneVerified?.(result.signup_token);
        } else {
          setCodeError(toI18N("lp_login_phone_verification_error"));
        }
        return;
      }

      if (result.id && result.refresh_token) {
        props.handlePhoneVerificationComplete(result.id, result.refresh_token, result.refresh_token_v2, result.birthday);
      } else {
        setCodeError(toI18N("lp_login_phone_verification_error"));
      }
    } catch (error: any) {
      if (
        error?.serverMessage === "signup token expired" ||
        error?.serverMessage === "invalid signup token"
      ) {
        // A dead pending-signup session isn't a code problem — leave the
        // screen, so a toast (not an inline field error) is right here.
        toast.error(toI18N("lp_login_signup_session_expired"));
        props.handleBack();
      } else {
        setCodeError(authErrorMessage(error));
      }
    } finally {
      setPhoneVerificationLoading(false);
    }
  };

  const handleKeyDown = (e: any) => {
    if (e.key === "Enter") {
      if (!showCodeInput) {
        handleSendCode();
      } else if (!verifyLock.active) {
        handleVerifyCode();
      }
    }
  };

  const handlePhoneNumberChange = (e: any) => {
    const input = e.target.value;
    setPhoneNumber(input);
    if (phoneError) setPhoneError("");

    const p = phone(`${countryCode}${input}`, {country: country});
    setPhoneNumberValid(p.isValid);
  };

  return (
    <Wrapper InIframe={inIframe} isMobile={size.width < 481} coverImage={props.coverImage} backgroundColor={props.backgroundColor}>
      <div className="p-8" aria-busy={props.checkingLocation || phoneVerificationLoading}>
        {/* Nav row: back control left, community lockup centered
            (symmetric flex-1 spacers keep it optically centered). */}
        <div className="mb-4 flex items-center">
          <div className="flex flex-1 justify-start">
            <button
              onClick={handleBackNavigation}
              className="p-2 bg-neutral-1 rounded-full hover:bg-neutral-11 text-primary-3"
              aria-label="Go back"
            >
              <img
                src={"/back_button.svg"}
                alt=""
                aria-hidden="true"
                className={`w-15 h-15`}
              />
            </button>
          </div>
          <CommunityBrandHeader logo={props.logo} title={props.title} />
          <div className="flex-1" aria-hidden="true" />
        </div>
        <div className="flex flex-col items-center">
          <img
            src={"/logo_phone.svg"}
            alt="Phone verification"
            className={`mb-3 w-20 h-20`}
          />
          <h1
            className="mb-5 text-[24px] pb-8 font-bold text-center"
            style={{
              fontSize: "24px",
              fontWeight: "700",
              lineHeight: "29px",
              letterSpacing: "0em",
            }}
          >
            {toI18N("lp_login_phone_verification_title")}
          </h1>
          <h2
            className="mb-5 pb-8"
            style={{
              fontSize: "16px",
              fontWeight: "300",
              lineHeight: "16px",
              letterSpacing: "0em",
              textAlign: "center",
            }}
          >
            {toI18N("lp_login_phone_verification_description")}
          </h2>
          
          {/* Phone number input section */}
          {!showCodeInput && (
            <div className="w-full">
              <fieldset>
                <legend className="sr-only">Phone number</legend>
                <div className="flex mb-3">
                  <div className="relative w-1/3">
                    <label htmlFor="country-code-input" className="sr-only">
                      Country code
                    </label>
                    <input
                      id="country-code-input"
                      type="text"
                      role="combobox"
                      aria-expanded={countryDropdownOpen}
                      aria-controls="country-listbox"
                      aria-autocomplete="list"
                      aria-activedescendant={countryDropdownOpen && filteredCountries[highlightIndex] ? `country-option-${highlightIndex}` : undefined}
                      className="w-full border border-neutral-11 p-3 rounded-l bg-primary-4"
                      style={{ borderRadius: "8px 0 0 8px" }}
                      value={countryQuery}
                      placeholder="Search country or code"
                      onChange={(e) => {
                        setCountryQuery(e.target.value);
                        setCountryDropdownOpen(true);
                        setHighlightIndex(0);
                      }}
                      onFocus={() => {
                        setCountryDropdownOpen(true);
                        setHighlightIndex(0);
                        setCountryQuery(""); // Clear query to show all countries
                      }}
                      onBlur={() => {
                        setCountryDropdownOpen(false);
                        const current = countryCodeList.find((i) => i.value === `${countryCode}|${country}`);
                        setCountryQuery(current ? current.label : `${countryCode} (${country})`);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'ArrowDown') {
                          e.preventDefault();
                          setCountryDropdownOpen(true);
                          setHighlightIndex((i) => Math.min(i + 1, filteredCountries.length - 1));
                        } else if (e.key === 'ArrowUp') {
                          e.preventDefault();
                          setHighlightIndex((i) => Math.max(i - 1, 0));
                        } else if (e.key === 'Enter') {
                          const item = filteredCountries[highlightIndex] || (filteredCountries.length === 1 ? filteredCountries[0] : undefined);
                          if (item) {
                            setCountryCode(item.value.split('|')[0]);
                            setCountry(item.value.split('|')[1]);
                            setPhoneNumber('');
                            setPhoneNumberValid(false);
                            setCountryQuery(item.label);
                            setCountryDropdownOpen(false);
                          } else {
                            const current = countryCodeList.find((i) => i.value === `${countryCode}|${country}`);
                            setCountryQuery(current ? current.label : `${countryCode} (${country})`);
                            setCountryDropdownOpen(false);
                          }
                        } else if (e.key === 'Escape') {
                          const current = countryCodeList.find((i) => i.value === `${countryCode}|${country}`);
                          setCountryQuery(current ? current.label : `${countryCode} (${country})`);
                          setCountryDropdownOpen(false);
                        }
                      }}
                    />
                    {countryDropdownOpen && (
                      <div
                        id="country-listbox"
                        role="listbox"
                        aria-label="Country codes"
                        className="absolute z-10 mt-1 w-full max-h-48 overflow-auto border border-neutral-11 bg-primary-4 rounded shadow"
                      >
                        {filteredCountries.length === 0 ? (
                          <div className="p-2 text-neutral-2">No matches</div>
                        ) : (
                          filteredCountries.map((item, idx) => (
                            <div
                              id={`country-option-${idx}`}
                              key={item.value}
                              role="option"
                              aria-selected={idx === highlightIndex}
                              className={`p-2 cursor-pointer flex justify-between ${idx === highlightIndex ? 'bg-primary-2' : ''}`}
                              onMouseEnter={() => setHighlightIndex(idx)}
                              onMouseDown={() => {
                                setCountryCode(item.value.split('|')[0]);
                                setCountry(item.value.split('|')[1]);
                                setPhoneNumber('');
                                setPhoneNumberValid(false);
                                setCountryQuery(item.label);
                                setCountryDropdownOpen(false);
                              }}
                            >
                              <span>{item.label}</span>
                              <span className="text-neutral-2">{item.name}</span>
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                  <label htmlFor="phone-number-input" className="sr-only">
                    Phone number
                  </label>
                  <input
                    id="phone-number-input"
                    type="tel"
                    placeholder={toI18N("lp_login_phone_placeholder")}
                    className={`w-2/3 border p-3 rounded-r bg-primary-4 ${
                      phoneError ? "border-red-500" : "border-neutral-11"
                    }`}
                    style={{ borderRadius: "0 8px 8px 0" }}
                    value={phoneNumber}
                    onChange={handlePhoneNumberChange}
                    onKeyDown={handleKeyDown}
                    autoComplete="tel-national"
                    aria-describedby={phoneError ? "phone-error" : undefined}
                    aria-invalid={phoneError ? "true" : undefined}
                  />
                </div>
              </fieldset>
              {phoneError && (
                <p
                  id="phone-error"
                  className="mb-3 w-full text-sm text-red-600"
                  role="alert"
                >
                  {phoneError}
                </p>
              )}
              <button
                onClick={handleSendCode}
                disabled={!phoneNumberValid}
                className={`w-full mb-3 p-3 border border-neutral-11 text-neutral-1 ${
                  phoneNumberValid
                    ? "bg-primary-1 hover:bg-primary-2"
                    : "bg-neutral-7 cursor-not-allowed"
                }`}
                style={{
                  borderRadius: "24px",
                  fontSize: "16px",
                  fontWeight: "700",
                  lineHeight: "18px",
                  letterSpacing: "0em",
                  textAlign: "center",
                }}
              >
                {toI18N("lp_login_phone_send_code")}
              </button>
            </div>
          )}
          
          {/* Verification code input section */}
          {showCodeInput && (
            <div className="w-full">
              {verifyLock.active && (
                <div className="mb-3 w-full rounded-lg border border-secondary-1/30 bg-secondary-1/10 px-4 py-2 text-center text-sm text-secondary-1">
                  {formatI18nKeys(
                    "lp_login_otp_backoff_notice",
                    String(verifyLock.seconds),
                  )}
                </div>
              )}
              <label htmlFor="sms-verification-code" className="sr-only">
                SMS verification code
              </label>
              <input
                id="sms-verification-code"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                placeholder={toI18N("lp_login_phone_code_placeholder")}
                className={`mb-3 w-full border p-3 rounded bg-primary-4 ${
                  codeError ? "border-red-500" : "border-neutral-11"
                }`}
                style={{ borderRadius: "8px" }}
                onKeyDown={handleKeyDown}
                onChange={(e) => {
                  // Only allow numeric input
                  const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setVerificationCode(value);
                  if (codeError) setCodeError("");
                }}
                value={verificationCode}
                autoFocus={true}
                autoComplete="one-time-code"
                disabled={verifyLock.active}
                aria-describedby={codeError ? "sms-code-error" : undefined}
                aria-invalid={codeError ? "true" : undefined}
              />
              {codeError && (
                <p
                  id="sms-code-error"
                  className="mb-3 -mt-1 w-full text-sm text-red-600"
                  role="alert"
                >
                  {codeError}
                </p>
              )}
              <button
                onClick={handleVerifyCode}
                disabled={verifyLock.active || !isValidVerificationCode(verificationCode)}
                className={`w-full mb-3 p-3 border border-neutral-11 text-neutral-1 ${
                  !verifyLock.active && isValidVerificationCode(verificationCode)
                    ? "bg-primary-1 hover:bg-primary-2"
                    : "bg-neutral-7 cursor-not-allowed"
                }`}
                style={{
                  borderRadius: "24px",
                  fontSize: "16px",
                  fontWeight: "700",
                  lineHeight: "18px",
                  letterSpacing: "0em",
                  textAlign: "center",
                }}
              >
                {toI18N("lp_login_phone_verify_code")}
              </button>
              
              <div className="text-center mt-4">
                <p>
                  {toI18N("lp_login_phone_verification_sent_to")} {countryCode} {formatPhoneNumber}
                </p>
              </div>

              <div className="mt-4 text-center text-sm">
                {resend.active ? (
                  <span className="text-neutral-10">
                    {toI18N("login_otp_resend_after")} {resend.seconds}s
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={handleResend}
                    disabled={resendLoading}
                    className="font-bold text-primary-2"
                  >
                    {toI18N("login_otp_resend_label")}
                  </button>
                )}
              </div>
            </div>
          )}
          
          {/* Additional info for Chinese users */}
          <div className="mt-6 text-sm text-center text-gray-600">
            <p>{toI18N("lp_login_phone_china_info")}</p>
          </div>
        </div>
        
        <Toaster position="bottom-center" />
        {(props.checkingLocation || phoneVerificationLoading) && (
          <div className="fixed inset-0 z-50">
            <div className="absolute inset-0 bg-primary-4 opacity-50" />
            <div className="absolute inset-0">
              <Loader />
            </div>
          </div>
        )}
      </div>
    </Wrapper>
  );
}